import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { adsApi } from '@/api/client';
import { formatCurrencyValue } from '@/api/keywordMetrics';
import { cn } from '@/utils/cn';

type EditablePortfolioBudgetCellProps = {
  profileId: string;
  portfolioId: string;
  budget: number | null | undefined;
  portfolioLabel?: string;
  onUpdated?: () => void;
  className?: string;
};

export function EditablePortfolioBudgetCell({
  profileId,
  portfolioId,
  budget,
  portfolioLabel,
  onUpdated,
  className,
}: EditablePortfolioBudgetCellProps) {
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [draftBudget, setDraftBudget] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const displayValue =
    budget != null && Number.isFinite(budget) ? formatCurrencyValue(budget) : '—';
  const editable = budget != null && Number.isFinite(budget) && budget > 0;

  const mutation = useMutation({
    mutationFn: async (newBudget: number) => {
      const res = await adsApi.updatePortfolioBudget(profileId, portfolioId, {
        budget: newBudget,
      });
      if (!res.success) throw new Error('Failed to update portfolio budget');
      return newBudget;
    },
    onSuccess: (newBudget) => {
      showToast(`Updated portfolio budget to ${formatCurrencyValue(newBudget)}`, 'success');
      setIsOpen(false);
      onUpdated?.();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to update budget';
      showToast(
        `Failed to update budget${portfolioLabel ? ` for ${portfolioLabel}` : ''}: ${message}`,
        'error'
      );
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const openEditor = () => {
    if (!editable || budget == null) return;
    setDraftBudget(String(budget));
    setIsOpen(true);
  };

  const handleApply = () => {
    const parsed = Number(draftBudget);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      showToast('Enter a valid budget greater than 0', 'error');
      return;
    }
    mutation.mutate(parsed);
  };

  if (!editable) {
    return <span className={className}>{displayValue}</span>;
  }

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      <button
        type="button"
        onClick={openEditor}
        className="font-bold text-foreground hover:text-brand hover:underline underline-offset-2"
        aria-label={portfolioLabel ? `Edit budget for ${portfolioLabel}` : 'Edit portfolio budget'}
      >
        {displayValue}
      </button>

      {isOpen ? (
        <div className="absolute right-0 top-full z-20 mt-1 w-44 rounded-xl border border-border bg-surface p-3 shadow-lg">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={draftBudget}
            onChange={(event) => setDraftBudget(event.target.value)}
            className="mb-2 text-right font-mono text-sm"
            autoFocus
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleApply();
              }
            }}
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setIsOpen(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleApply} disabled={mutation.isPending}>
              {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Apply'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
